import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { HomeMoment, HomeMomentsPage } from './home-types';

/**
 * The moments module: opens with 5, one tap extends inline to 10, and past ten
 * a pager takes over — pages of ten, newest first, swapped in place.
 *
 * Deep-link target: the replay viewer at the moment's race. The exact
 * clip-open parameter is wired by the video workstream; the row links to the
 * race so the tap is never dead in the meantime.
 */

const CHIP: Record<string, { label: string; cls: string }> = {
  cross: { label: 'Cross', cls: 'text-viewer-accent bg-viewer-accent/10 border-viewer-accent/30' },
  mark_rounding: { label: 'Mark rounding', cls: 'text-viewer-gold bg-viewer-gold/10 border-viewer-gold/30' },
  start: { label: 'Start', cls: 'text-viewer-good bg-viewer-good/10 border-viewer-good/30' },
  finish: { label: 'Finish', cls: 'text-viewer-good bg-viewer-good/10 border-viewer-good/30' },
  speed_peak: { label: 'Speed peak', cls: 'text-viewer-coral bg-viewer-coral/10 border-viewer-coral/30' },
  overtake: { label: 'Overtake', cls: 'text-viewer-amber bg-viewer-amber/10 border-viewer-amber/30' },
  tack: { label: 'Tack', cls: 'text-viewer-violet bg-viewer-violet/10 border-viewer-violet/30' },
  jibe: { label: 'Jibe', cls: 'text-viewer-violet bg-viewer-violet/10 border-viewer-violet/30' },
  crowd: { label: 'Traffic', cls: 'text-viewer-amber bg-viewer-amber/10 border-viewer-amber/30' },
  sustained_close: { label: 'Close racing', cls: 'text-viewer-accent bg-viewer-accent/10 border-viewer-accent/30' },
  broach: { label: 'Broach', cls: 'text-viewer-warn bg-viewer-warn/10 border-viewer-warn/30' },
};

function fmtDuration(s?: number): string | null {
  if (!s || s <= 0) return null;
  return `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;
}

function MomentRow({ m, their }: { m: HomeMoment; their: boolean }) {
  const chip = CHIP[m.type] ?? CHIP.cross;
  const duration = fmtDuration(m.durationS);
  void their;
  return (
    <Link
      // The API resolves each moment's own clip, seek time and camera aim
      // (`buildMomentLink`). The race is only the FALLBACK, for a moment served
      // by a build predating `link` — opening the start of a two-hour race is
      // poor, but it beats a dead card.
      to={m.link ?? `/view/${m.raceId}`}
      className="mb-2.5 flex items-start gap-3 rounded-xl border border-viewer-line/25 bg-viewer-panel px-3.5 py-3 transition-colors hover:border-viewer-line2/40"
    >
      <span
        className={`mt-0.5 shrink-0 rounded-md border px-2 py-0.5 font-viewer-mono text-[9px] uppercase tracking-wider ${chip.cls}`}
      >
        {chip.label}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-semibold text-viewer-ink">{m.title}</span>
        {m.description && (
          <span className="mt-0.5 hidden text-[12px] text-viewer-muted sm:block">{m.description}</span>
        )}
        <span className="mt-1 block font-viewer-mono text-[10px] text-viewer-faint">
          <span className="text-viewer-accent">
            {m.regattaShort} · R{m.raceNumber}
          </span>
          {duration && <> · {duration} clip</>}
        </span>
      </span>
      <span className="self-center text-[13px] font-bold text-viewer-accent">▶</span>
    </Link>
  );
}

export function MomentsFeed({
  initial,
  their = false,
  fetchPage,
}: {
  initial: HomeMomentsPage;
  /** Supporter copy: the boat's moments, not yours. */
  their?: boolean;
  /** Load a page (1-based). Provided by the page; backed by GET /me/moments. */
  fetchPage: (page: number) => Promise<HomeMomentsPage>;
}) {
  const [items, setItems] = useState<HomeMoment[]>(initial.items.slice(0, 5));
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const total = initial.total;
  const pageCount = Math.max(1, Math.ceil(total / 10));

  // 5 → 10: reveal inline from what we already have, fetching only if the
  // first response carried fewer than ten.
  async function showMore() {
    setLoading(true);
    try {
      let ten = initial.items;
      if (ten.length < Math.min(10, total)) ten = (await fetchPage(1)).items;
      setItems(ten.slice(0, 10));
      setExpanded(true);
    } finally {
      setLoading(false);
    }
  }

  async function goPage(p: number) {
    if (p < 1 || p > pageCount || p === page) return;
    setLoading(true);
    try {
      const res = await fetchPage(p);
      setItems(res.items);
      setPage(p);
    } finally {
      setLoading(false);
    }
  }

  if (total === 0) return null;

  return (
    <div>
      <div className="mb-3 flex items-baseline gap-2.5">
        <h2 className="font-viewer-display text-[14px] font-bold uppercase tracking-[.13em] text-viewer-ink">
          {their ? 'Their moments' : 'Your moments'}
        </h2>
        <span className="text-[11px] text-viewer-faint">{total} total</span>
      </div>

      {items.map((m) => (
        <MomentRow key={m.momentId} m={m} their={their} />
      ))}

      {!expanded && total > 5 && (
        <button
          onClick={showMore}
          disabled={loading}
          className="mt-1 block w-full rounded-lg border border-dashed border-viewer-line2/40 py-2.5 text-center text-[12.5px] font-semibold text-viewer-accent hover:border-viewer-accent/50 disabled:opacity-60"
        >
          {loading ? 'Loading…' : `Show ${Math.min(5, total - 5)} more`}
        </button>
      )}

      {expanded && total > 10 && (
        <div className="mt-3 flex items-center justify-center gap-1.5">
          <button
            onClick={() => goPage(page - 1)}
            disabled={page === 1 || loading}
            className="flex h-7 min-w-7 items-center justify-center rounded-md border border-viewer-line2/40 font-viewer-mono text-[11px] text-viewer-faint disabled:opacity-40"
            aria-label="Previous page"
          >
            ‹
          </button>
          {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => goPage(p)}
              disabled={loading}
              className={`flex h-7 min-w-7 items-center justify-center rounded-md border font-viewer-mono text-[11px] ${
                p === page
                  ? 'border-viewer-accent bg-viewer-accent/15 font-bold text-viewer-ink'
                  : 'border-viewer-line2/40 text-viewer-muted'
              }`}
            >
              {p}
            </button>
          ))}
          <button
            onClick={() => goPage(page + 1)}
            disabled={page === pageCount || loading}
            className="flex h-7 min-w-7 items-center justify-center rounded-md border border-viewer-line2/40 font-viewer-mono text-[11px] text-viewer-faint disabled:opacity-40"
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
